import { lumi } from '@/lib/lumi'
import { useEffect, useState } from 'react'

const ACCENT = '#C9A96E'
const ACCENT_SOFT = '#e0c898'

interface DiamItem {
  _id: string
  name?: string
  whatsapp?: string
  email?: string
  q4?: string
  q5?: string
  q6?: string
  q7?: string
  q8?: string
  q9?: string
  q10?: string
  q11?: string
  q12?: string
  q13?: string
  q14?: string
  createdAt: string
}

const BLOCK_QUESTIONS: Array<{
  title: string
  questions: Array<{ key: keyof DiamItem; label: string }>
}> = [
  {
    title: 'BLOCO 1 — Identificação',
    questions: [
      { key: 'name', label: 'Qual é o seu nome completo?' },
      { key: 'whatsapp', label: 'Qual é o seu WhatsApp com DDD?' },
      { key: 'email', label: 'Qual é o seu email?' },
    ],
  },
  {
    title: 'BLOCO 2 — Quem você é',
    questions: [
      { key: 'q4', label: 'Como você chegou até aqui?' },
      { key: 'q5', label: 'Você já viveu alguma experiência com o meu trabalho?' },
    ],
  },
  {
    title: 'BLOCO 3 — O que você carrega',
    questions: [
      { key: 'q6', label: 'O que você está buscando transformar de verdade?' },
      { key: 'q7', label: 'Há quanto tempo você carrega isso e como se manifesta?' },
      { key: 'q8', label: 'O que você já tentou antes para resolver isso?' },
      { key: 'q9', label: 'Existe algo que você nunca contou a ninguém?' },
    ],
  },
  {
    title: 'BLOCO 4 — Comprometimento e disponibilidade',
    questions: [
      { key: 'q10', label: 'Disponibilidade para 126 dias?' },
      { key: 'q11', label: 'É um momento de vida em que você pode priorizar sua transformação?' },
      { key: 'q12', label: 'O que faria você desistir no meio do caminho?' },
    ],
  },
  {
    title: 'BLOCO 5 — O que você espera',
    questions: [
      { key: 'q13', label: 'Como seria sua vida ao final dos 126 dias?' },
      { key: 'q14', label: 'O que você quer que a Mônica saiba sobre você?' },
    ],
  },
]

function formatDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch { return iso }
}

export default function AdminDiamante() {
  const [items, setItems] = useState<DiamItem[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    fetchItems()
  }, [])

  async function fetchItems() {
    setLoading(true)
    try {
      const { list } = await lumi.entities.diamante_formulario.list({
        sort: { createdAt: -1 },
        limit: 300,
      })
      setItems(list as DiamItem[])
    } catch (e) {
      console.error('Erro ao buscar formulários Diamante:', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Deseja excluir este formulário? Essa ação é permanente.')) return
    setDeletingId(id)
    try {
      await lumi.entities.diamante_formulario.delete(id)
      setItems(prev => prev.filter(i => i._id !== id))
      if (expandedId === id) setExpandedId(null)
    } catch (e) {
      console.error('Erro ao excluir:', e)
      alert('Erro ao excluir. Tente novamente.')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${ACCENT} transparent transparent transparent` }} />
          <p className="text-[13px] font-body text-[var(--ink-soft)]">Carregando formulários…</p>
        </div>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <svg width="36" height="36" viewBox="0 0 56 56" fill="none" className="mb-4 opacity-30" aria-hidden="true">
          <polygon points="28,4 52,21 28,52 4,21" fill="none" stroke={ACCENT} strokeWidth="1.5" />
        </svg>
        <p className="text-[15px] font-display italic text-[var(--ink-soft)] mb-2">Nenhum formulário enviado ainda</p>
        <p className="text-[12px] font-body text-[var(--ink-soft)] opacity-60">
          Os formulários do Reacenda o Diamante aparecerão aqui após o envio.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Count */}
      <p className="text-[12px] font-body text-[var(--ink-soft)] opacity-60 mb-4 numerals">
        {items.length} {items.length === 1 ? 'formulário' : 'formulários'} recebido{items.length === 1 ? '' : 's'}
      </p>

      {items.map(item => {
        const isExpanded = expandedId === item._id
        const name = item.name || '—'
        const contactLine = [item.whatsapp, item.email].filter(Boolean).join(' · ')

        return (
          <div
            key={item._id}
            className="rounded-2xl border border-white/8 overflow-hidden transition-all duration-200"
            style={{ background: 'rgba(255,255,255,0.025)' }}
          >
            {/* Header row */}
            <div className="flex items-center justify-between gap-3 p-4 sm:p-5">
              <div className="min-w-0 flex-1">
                {/* Diamond badge */}
                <div className="flex items-center gap-2 mb-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <polygon points="12,2 22,9 12,22 2,9" fill="none" stroke={ACCENT} strokeWidth="1.5" />
                  </svg>
                  <span className="text-[10px] tracking-[0.25em] uppercase font-body" style={{ color: ACCENT_SOFT }}>
                    Reacenda o Diamante
                  </span>
                </div>
                <p className="font-display italic text-[17px] sm:text-[18px] text-[var(--ink)] leading-snug truncate">
                  {name}
                </p>
                {contactLine && (
                  <p className="text-[12px] text-[var(--ink-soft)] font-body mt-0.5 truncate numerals">
                    {contactLine}
                  </p>
                )}
                <p className="text-[12px] text-[var(--ink-soft)] font-body mt-0.5 opacity-70 numerals">
                  {formatDate(item.createdAt)}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Toggle expand */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : item._id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-body tracking-[0.1em] uppercase transition-all duration-150"
                  style={{
                    color: ACCENT_SOFT,
                    background: `${ACCENT}14`,
                    border: `1px solid ${ACCENT}33`,
                  }}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? 'Fechar' : 'Ver respostas'}
                  <svg
                    className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Delete */}
                <button
                  onClick={() => handleDelete(item._id)}
                  disabled={deletingId === item._id}
                  className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 hover:bg-red-500/15 active:scale-90 group"
                  title="Excluir formulário"
                  aria-label="Excluir formulário"
                >
                  <svg className="w-4 h-4 text-[var(--ink-soft)] group-hover:text-red-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Expanded answers */}
            {isExpanded && (
              <div
                className="px-4 sm:px-5 pb-6 border-t"
                style={{ borderColor: `${ACCENT}22` }}
              >
                {BLOCK_QUESTIONS.map((block, bi) => (
                  <div key={bi} className="mt-6">
                    <p
                      className="text-[10px] tracking-[0.3em] uppercase font-body mb-4 pb-2 border-b"
                      style={{ color: ACCENT_SOFT, borderColor: `${ACCENT}22` }}
                    >
                      {block.title}
                    </p>
                    <div className="space-y-5">
                      {block.questions.map(q => {
                        const value = item[q.key] as string | undefined
                        return (
                          <div key={String(q.key)}>
                            <p className="text-[11px] tracking-[0.1em] uppercase font-body mb-1.5 opacity-60" style={{ color: ACCENT_SOFT }}>
                              {q.label}
                            </p>
                            <p className="font-body text-[14px] sm:text-[15px] text-[var(--ink)] leading-relaxed whitespace-pre-wrap">
                              {value && value.trim() ? value : <span className="opacity-40 italic">—</span>}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
